import React from "react";
import { motion } from "framer-motion";
import { Shield, Radio, Lock, Zap, Plane, Package } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";

export default function InvestmentFocus() {
  const focusAreas = [
    {
      icon: Shield,
      title: "Advanced Materials & Survivability",
      description: "Protective materials and systems enhancing resilience and platform performance.",
      tags: ["Armor & Composites", "Thermal Protection", "Blast & Mine Resistance"]
    },
    {
      icon: Radio,
      title: "Sensing, EW & Situational Awareness",
      description: "Sensors, RF systems and electronic warfare enabling detection, protection and dominance.",
      tags: ["Sensors & RF", "Electronic Warfare", "Drone-Defence Systems"]
    },
    {
      icon: Lock,
      title: "Embedded Electronics & Cyber Security",
      description: "Mission computers, secure communications and cyber-protection for critical systems.",
      tags: ["Secure Comms", "Encryption Modules", "Critical Infrastructure Protection"]
    },
    {
      icon: Zap,
      title: "Energy, Mobility & Propulsion",
      description: "Next-gen energy, power and mobility solutions for manned and unmanned platforms.",
      tags: ["Power & Thermal Systems", "Propulsion", "Special Tires & Mobility Solutions"]
    },
    {
      icon: Plane,
      title: "Autonomy & Unmanned Systems",
      description: "Dual-use autonomous platforms for land, air, sea and sub-surface missions.",
      tags: ["UAVs", "UGVs", "USVs & UUVs"]
    },
    {
      icon: Package,
      title: "Defence Logistics & Mission Support",
      description: "Digital logistics, MRO and supply-chain intelligence ensuring operational readiness.",
      tags: ["Predictive Logistics", "Fleet & MRO Solutions", "Mine Clearance & Obstacle Overcoming"]
    }
  ];

  return (
    <div className="bg-[#0B1C2C] py-24">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            What We Invest In
          </h2>
          <p className="text-xl text-[#6B7C93] max-w-3xl mx-auto mb-8">
            We target companies operating in niche, high-impact segments of the defence supply chain, providing mission-critical, cutting-edge and dual-use technologies with broad platform fit and strong scalability across civilian and military applications.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-[#6B7C93]">Stage:</span>
              <Badge variant="outline" className="bg-[#24A19C]/10 border-[#24A19C] text-[#24A19C]">
                Late-seed to Profitable SME
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#6B7C93]">Ticket:</span>
              <Badge variant="outline" className="bg-[#E0B422]/10 border-[#E0B422] text-[#E0B422]">
                Growth Equity & Structured Solutions
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#6B7C93]">Geography:</span>
              <Badge variant="outline" className="bg-white/10 border-white/30 text-white">
                DACH, Western Europe, Ukraine, Middle East
              </Badge>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {focusAreas.map((area, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="bg-[#0F2437] border-[#24A19C]/20 hover:border-[#24A19C]/50 transition-all duration-300 h-full group">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <div className="bg-[#24A19C]/10 p-4 rounded-xl w-fit group-hover:bg-[#24A19C]/20 transition-colors">
                      <area.icon className="w-8 h-8 text-[#24A19C]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {area.title}
                  </h3>
                  <p className="text-[#6B7C93] mb-4 leading-relaxed">
                    {area.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {area.tags.map((tag, tagIndex) => (
                      <Badge 
                        key={tagIndex}
                        variant="outline"
                        className="bg-[#24A19C]/5 border-[#24A19C]/30 text-[#24A19C] text-xs"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}